import React, { useState, useEffect, useRef } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { updateFoodCourtItem, getFoodCourtItemById } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import { 
  IndividualEditPageLayout, 
  SidebarSection, 
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { 
  Utensils,
  Tag, 
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Coffee,
  Star,
  Clock
} from 'lucide-react';
import { MenuUploadField } from './MenuUploadField';
import type { FoodCourtMenuMetadata } from './menuTypes';
import { getFoodCourtMenu } from './menuTypes';

interface FoodCourtFormData {
  title: string;
  description: string;
  menu: FoodCourtMenuMetadata | null;
  category: 'FOOD' | 'BEVERAGE' | 'DESSERT' | 'SNACK';
  cuisine: string;
  dietaryInfo: string[];
  availability: 'AVAILABLE' | 'LIMITED' | 'OUT_OF_STOCK';
  contactInfo: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const FOOD_CATEGORIES = [
  { value: 'FOOD', label: 'Food', icon: Utensils },
  { value: 'BEVERAGE', label: 'Beverage', icon: Coffee },
  { value: 'DESSERT', label: 'Dessert', icon: Star },
  { value: 'SNACK', label: 'Snack', icon: Tag }
];

const AVAILABILITY_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available', description: 'Currently available for order' },
  { value: 'LIMITED', label: 'Limited', description: 'Limited quantity available' },
  { value: 'OUT_OF_STOCK', label: 'Out of Stock', description: 'Currently unavailable' }
];

const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut-Free', 
  'Local', 'Kosher', 'Low-Carb', 'Keto', 'Organic'
];

const VISIBILITY_OPTIONS = [
  { value: 'VERIFIED', label: 'Verified Members', description: 'Visible to verified members only' },
  { value: 'PENDING', label: 'Pending Review', description: 'Will be reviewed before publishing' }
];

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function EditFoodCourtPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { itemId, slug } = useParams<{ itemId: string; slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const foodCourtListPath = locationSectionPath(locSlug, 'food-court');
  
  const updateFoodCourtItemFn = useAction(updateFoodCourtItem);
  
  const { data: item, isLoading, error } = useQuery(getFoodCourtItemById, { itemId: itemId || '' });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FoodCourtFormData>({
    title: '',
    description: '',
    menu: null,
    category: 'FOOD',
    cuisine: '',
    dietaryInfo: [],
    availability: 'AVAILABLE',
    contactInfo: '',
    visibility: 'VERIFIED'
  });

  const hydratedForItemId = useRef<string | null>(null);

  // Populate form once when item data first loads (avoid wiping unsaved edits on refetch).
  useEffect(() => {
    hydratedForItemId.current = null;
  }, [itemId]);

  useEffect(() => {
    if (!item || !itemId) return;
    if (hydratedForItemId.current === itemId) return;

    hydratedForItemId.current = itemId;
    const itemData = item as any;
    setFormData({
      title: itemData.title || '',
      description: itemData.description || '',
      menu: getFoodCourtMenu(itemData.metadata) ?? null,
      category: itemData.metadata?.category || 'FOOD',
      cuisine: itemData.metadata?.cuisine || '',
      dietaryInfo: itemData.metadata?.dietaryInfo || [],
      availability: itemData.metadata?.availability || 'AVAILABLE',
      contactInfo: itemData.metadata?.contactInfo || '',
      visibility: itemData.visibility || 'VERIFIED',
    });
  }, [item, itemId]);

  const handleChange = (field: keyof FoodCourtFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDietaryChange = (diet: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      dietaryInfo: checked 
        ? [...prev.dietaryInfo, diet]
        : prev.dietaryInfo.filter(d => d !== diet)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!itemId) {
      setErrors({ submit: 'Item ID is missing' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await updateFoodCourtItemFn({
        itemId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        cuisine: formData.cuisine.trim(),
        dietaryInfo: formData.dietaryInfo,
        availability: formData.availability,
        contactInfo: formData.contactInfo.trim(),
        ...(isAdministrator(user) ? { menu: formData.menu } : {}),
      });

      // Navigate back to the food court page
      handleGoBack();
    } catch (error) {
      console.error('Error updating food court item:', error);
      setErrors({ submit: 'Failed to update item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(foodCourtListPath);
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to edit food court items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading item...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Item Not Found</h3>
            <p className="text-gray-600 mb-4">The item you're looking for doesn't exist or you don't have permission to edit it.</p>
            <button
              onClick={handleGoBack}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Check if user can edit this item
  if ((item as any).authorId !== user.id && !isAdministrator(user)) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600 mb-4">You can only edit your own items.</p>
            <button
              onClick={handleGoBack}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="food-court-form"
            disabled={isSubmitting || !formData.title.trim()}
            icon={Save}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>
          
          <ActionButtonSecondary
            onClick={handleGoBack}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Visibility Settings">
        <div className="space-y-3">
          {VISIBILITY_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="visibility"
                value={option.value}
                checked={formData.visibility === option.value}
                onChange={(e) => handleChange('visibility', e.target.value as 'VERIFIED' | 'PENDING')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900">{option.label}</div>
                <Tooltip content={option.description}>
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>
          ))}
        </div>
      </SidebarSection>

      <SidebarSection title="Tips for Better Results">
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Use appetizing titles that describe the dish clearly</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Include key ingredients and preparation details</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Upload a PDF or image so visitors can view your menu from the list</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Provide accurate dietary information for customer safety</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Keep availability status updated to avoid confusion</span>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent}>
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Utensils className="w-8 h-8 text-blue-600" />
                        <div>
                          <h1 className="text-2xl font-bold text-gray-900">Edit Food Court Item</h1>
                          <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                        </div>
                      </div>
                    </div>

                          <form id="food-court-form" onSubmit={handleSubmit}>
                      {/* Full-width sections */}
                      <div className="space-y-6 mb-6">
                        {/* What are you posting? */}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">What are you offering?</h3>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                              errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                            }`}
                            placeholder="e.g., Chicken Teriyaki Bowl, Fresh Smoothie, Chocolate Cake"
                            required
                          />
                          {errors.title && (
                            <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.title}
                            </p>
                          )}
                        </div>

                        {/* Description */}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                          <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            rows={4}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                              errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                            }`}
                            placeholder="Describe your food item, ingredients, preparation method, special features..."
                            required
                          />
                          {errors.description && (
                            <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.description}
                            </p>
                          )}
                          <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                        </div>
                      </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category and Cuisine */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleChange('category', e.target.value as 'FOOD' | 'BEVERAGE' | 'DESSERT' | 'SNACK')}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {FOOD_CATEGORIES.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Cuisine Type</label>
                        <input
                          type="text"
                          value={formData.cuisine}
                          onChange={(e) => handleChange('cuisine', e.target.value)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="e.g., Italian, Asian, Mexican"
                        />
                      </div>
                    </div>

                    {/* Menu (admin) and Availability */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {isAdministrator(user) && (
                        <div>
                          <MenuUploadField
                            locationSlug={locSlug ?? 'location'}
                            itemKey={itemId || 'edit'}
                            contentId={itemId}
                            value={formData.menu}
                            onChange={(menu) => handleChange('menu', menu)}
                            disabled={isSubmitting}
                          />
                        </div>
                      )}

                      <div className={isAdministrator(user) ? '' : 'sm:col-span-2'}>
                        <label className="block text-sm font-medium text-foreground mb-2">Availability</label>
                        <select
                          value={formData.availability}
                          onChange={(e) => handleChange('availability', e.target.value as 'AVAILABLE' | 'LIMITED' | 'OUT_OF_STOCK')}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {AVAILABILITY_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Dietary Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Dietary Information</span>
                          <Tooltip content="Select all that apply to help customers with dietary restrictions">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {DIETARY_OPTIONS.map(diet => (
                          <label key={diet} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.dietaryInfo.includes(diet)}
                              onChange={(e) => handleDietaryChange(diet, e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{diet}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Contact Information</span>
                          <Tooltip content="How customers can reach you for orders">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.contactInfo}
                          onChange={(e) => handleChange('contactInfo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Phone number or email"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
    </IndividualEditPageLayout>
  );
}



